import { useEffect, useState } from "react";
import { api, BACKEND_URL } from "../lib/api";
import { Modal } from "./ui";
import { Paperclip, Plus, Upload, Trash2 } from "lucide-react";

/**
 * Per-row evidence cell. Shows:
 *   - "+ Agregar" pill when count==0
 *   - "📎 N" green badge when count>0
 * Clicking opens an attached mini-modal with the upload zone + list of existing
 * files scoped to this specific record (module=collection, parent_id=row.id).
 */
export default function EvidenceCell({ collection, row, testIdPrefix }) {
  const [count, setCount] = useState(null);
  const [open, setOpen] = useState(false);

  async function refresh() {
    try {
      const { data } = await api.get("/files/list", { params: { module: collection, parent_id: row.id } });
      setCount(Array.isArray(data) ? data.length : 0);
    } catch {
      setCount(0);
    }
  }
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [row.id]);

  return (
    <>
      {count === null ? (
        <span className="text-xs text-dim">…</span>
      ) : count > 0 ? (
        <button
          className="badge badge-green"
          onClick={(e) => { e.stopPropagation(); setOpen(true); }}
          style={{ cursor: "pointer", border: 0 }}
          data-testid={`${testIdPrefix}-evidence-${row.id}`}
          title="Ver/Agregar evidencias"
        >
          <Paperclip size={11} /> {count}
        </button>
      ) : (
        <button
          className="btn btn-sm"
          onClick={(e) => { e.stopPropagation(); setOpen(true); }}
          style={{ fontSize: 11, padding: "2px 6px" }}
          data-testid={`${testIdPrefix}-evidence-${row.id}`}
        >
          <Plus size={11} /> Agregar
        </button>
      )}
      <Modal open={open} onClose={() => { setOpen(false); refresh(); }} title="Evidencias del registro" size="lg" testId={`${testIdPrefix}-evidence-modal-${row.id}`}>
        <EvidencePanel collection={collection} parentId={row.id} onChange={refresh} />
      </Modal>
    </>
  );
}

function EvidencePanel({ collection, parentId, onChange }) {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [active, setActive] = useState(false);

  async function load() {
    const { data } = await api.get("/files/list", { params: { module: collection, parent_id: parentId } });
    setFiles(data);
    onChange && onChange();
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [parentId]);

  async function upload(fileList) {
    if (!fileList || !fileList.length) return;
    setUploading(true);
    for (const f of fileList) {
      const form = new FormData();
      form.append("file", f);
      form.append("module", collection);
      form.append("parent_id", parentId);
      try { await api.post("/files/upload", form, { headers: { "Content-Type": "multipart/form-data" } }); }
      catch (e) { console.error(e); }
    }
    setUploading(false);
    load();
  }

  async function rename(file) {
    const newName = window.prompt("Nuevo nombre:", file.name || file.original_name);
    if (!newName) return;
    try { await api.patch(`/files/${file.id}`, { name: newName }); load(); } catch (e) { alert(e.message); }
  }
  async function del(file) {
    if (!window.confirm("¿Eliminar evidencia?")) return;
    await api.delete(`/files/${file.id}`);
    load();
  }

  return (
    <div className="grid gap-3">
      <div
        className={`dropzone ${active ? "active" : ""}`}
        onClick={() => document.getElementById(`ev-input-${parentId}`)?.click()}
        onDragOver={(e) => { e.preventDefault(); setActive(true); }}
        onDragLeave={() => setActive(false)}
        onDrop={(e) => { e.preventDefault(); setActive(false); upload(e.dataTransfer.files); }}
      >
        <input id={`ev-input-${parentId}`} type="file" multiple style={{ display: "none" }} accept=".jpg,.jpeg,.png,.pdf,.docx,.xlsx" onChange={(e) => upload(e.target.files)} />
        {uploading ? <div className="flex items-center justify-center gap-2"><span className="spinner" /> Subiendo...</div> : (
          <div>
            <div className="text-lg"><Upload size={16} style={{ display: "inline" }} /> Arrastra archivos o haz clic para subir</div>
            <div className="text-xs text-dim mt-2">JPG · PNG · PDF · DOCX · XLSX</div>
          </div>
        )}
      </div>

      {files.length === 0 ? (
        <div className="text-dim text-sm text-center p-3">Aún no hay evidencias para este registro.</div>
      ) : (
        <div className="grid gap-2">
          {files.map((f) => (
            <div key={f.id} className="card" style={{ padding: 10 }}>
              <div className="flex items-center justify-between gap-3">
                <div className="flex-1" style={{ minWidth: 0 }}>
                  <div className="font-semibold text-sm" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    📄 {f.name || f.original_name}
                  </div>
                  <div className="text-xs text-dim mt-2">
                    {((f.size || 0) / 1024).toFixed(1)} KB · {f.uploaded_by_name || ""} · {f.created_at ? new Date(f.created_at).toLocaleDateString("es-MX") : ""}
                  </div>
                </div>
                <div className="flex gap-1">
                  <a className="btn btn-sm" href={`${BACKEND_URL}/api/files/view/${f.id}`} target="_blank" rel="noreferrer">Ver</a>
                  <a className="btn btn-sm" href={`${BACKEND_URL}/api/files/download/${f.id}`} download>Descargar</a>
                  <button className="btn btn-sm" onClick={() => rename(f)}>Renombrar</button>
                  <button className="btn btn-sm btn-danger" onClick={() => del(f)}><Trash2 size={12} /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
